import { ref } from 'vue';
import { cloneDeep } from 'lodash';
import { useStore } from '@/store';

const DASHBOARD_QUERY_STORAGE_KEY = 'dashboard-query';

const readDashboardQueryFromStorage = (): Record<string, unknown> => {
  const cached = window.localStorage.getItem(DASHBOARD_QUERY_STORAGE_KEY);
  return cached ? JSON.parse(cached) : {};
};

export type DashboardPeriodPayload = {
  from: string;
  to: string;
  date: string;
  timezone?: string;
};

export const createDashboardModule = () => {
  const dashboardQuery = ref<Record<string, unknown>>(readDashboardQueryFromStorage());
  const dashboardQueryTemporary = ref<Record<string, unknown>>(readDashboardQueryFromStorage());
  const dashboardSortBy = ref<
    { title?: string; value?: string } & Record<string, unknown>
  >({});
  const dashboardOrderDir = ref<string>('asc');

  const persistDashboardQuery = () => {
    window.localStorage.setItem(
      DASHBOARD_QUERY_STORAGE_KEY,
      JSON.stringify(dashboardQuery.value),
    );
  };

  const patchDashboardQueryTemporaryField = (data: { name: string; value: unknown }) => {
    dashboardQueryTemporary.value[data.name] = data.value;
  };

  const patchDashboardQueryField = (data: {
    name: string;
    value: unknown;
    notSetToLocalStorage?: boolean;
  }) => {
    if (data.notSetToLocalStorage) return;

    dashboardQuery.value[data.name] = data.value;
    persistDashboardQuery();
  };

  const applyDashboardQuery = () => {
    dashboardQuery.value = cloneDeep(dashboardQueryTemporary.value);
    persistDashboardQuery();
  };

  const restoreDashboardQuery = () => {
    dashboardQueryTemporary.value = cloneDeep(dashboardQuery.value);
  };

  const patchDashboardQueryPeriod = (
    period: DashboardPeriodPayload & { notSetToLocalStorage?: boolean },
  ) => {
    if (period.notSetToLocalStorage) return;

    dashboardQuery.value.date = period.date;
    dashboardQuery.value.from = period.from;
    dashboardQuery.value.to = period.to;
    dashboardQuery.value.timezone =
      period.timezone || (useStore().user.defaultTimezone as string);
    persistDashboardQuery();
  };

  const patchDashboardQueryPeriodTemporary = (period: DashboardPeriodPayload) => {
    dashboardQueryTemporary.value.date = period.date;
    dashboardQueryTemporary.value.from = period.from;
    dashboardQueryTemporary.value.to = period.to;
    dashboardQueryTemporary.value.timezone =
      period.timezone || (useStore().user.defaultTimezone as string);
  };

  const setDashboardDirection = (direction: string) => {
    dashboardQuery.value.direction = direction;
    persistDashboardQuery();
  };

  const setDashboardSortBy = (sorting: { title: string; value?: string }) => {
    dashboardSortBy.value = { title: sorting.title, value: sorting.value };
    if (sorting.value) {
      dashboardQuery.value.orderBy = sorting.value;
      persistDashboardQuery();
    }
  };

  const reset = () => {
    dashboardQuery.value = {};
    dashboardQueryTemporary.value = {};
    dashboardSortBy.value = {};
    dashboardOrderDir.value = 'asc';
  };

  return {
    state: {
      dashboardQuery,
      dashboardQueryTemporary,
      dashboardSortBy,
      dashboardOrderDir,
      patchDashboardQueryTemporaryField,
      patchDashboardQueryField,
      applyDashboardQuery,
      restoreDashboardQuery,
      patchDashboardQueryPeriod,
      patchDashboardQueryPeriodTemporary,
      setDashboardDirection,
      setDashboardSortBy,
    },
    reset,
  };
};
